
import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Bell, Trash2 } from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { Notification } from "@/hooks/useNotifications";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

export function NotificationCenter() {
  const { 
    useNotificationsQuery, 
    useUnreadCountQuery,
    useMarkAsReadMutation,
    useMarkAllAsReadMutation,
    useDeleteReadNotificationsMutation
  } = useNotifications();
  const { data: notifications = [], isLoading } = useNotificationsQuery();
  const { data: unreadCount = 0 } = useUnreadCountQuery();
  const markAsRead = useMarkAsReadMutation();
  const markAllAsRead = useMarkAllAsReadMutation();
  const deleteReadNotifications = useDeleteReadNotificationsMutation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Ensure unreadCount is treated as a number
  const unreadCountNumber = typeof unreadCount === 'number' ? unreadCount : 0;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead.mutate(notification.id);
    
    if (notification.contractId) {
      navigate(`/contracts/${notification.contractId}`);
      setOpen(false);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead.mutate();
  };

  const handleClearReadNotifications = () => {
    deleteReadNotifications.mutate();
  };

  const getNotificationTypeStyle = (type: string) => {
    switch (type) {
      case "reminder": 
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "system": 
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "info":
      default: 
        return "bg-green-100 text-green-800 border-green-200";
    }
  };

  // Check if there are any read notifications
  const hasReadNotifications = notifications.some(notification => notification.isRead);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {unreadCountNumber > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 px-1.5 py-0.5 min-w-[1.2rem] h-5 flex items-center justify-center bg-red-500"
              variant="destructive"
            >
              {unreadCountNumber > 99 ? "99+" : unreadCountNumber}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          <div className="flex gap-2">
            {unreadCountNumber > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleMarkAllAsRead} 
                className="text-xs h-7"
              >
                Mark all as read
              </Button>
            )}
            {hasReadNotifications && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleClearReadNotifications} 
                className="text-xs h-7 text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center gap-1"
              >
                <Trash2 className="h-3 w-3" />
                Clear read
              </Button>
            )}
          </div>
        </div>
        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="p-4 text-center text-muted-foreground">Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">No notifications</div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <div key={notification.id}>
                  <Card 
                    className={`m-2 p-3 cursor-pointer border-l-4 ${
                      notification.isRead ? "bg-muted/50" : "border-l-primary"
                    } ${getNotificationTypeStyle(notification.type)}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <h4 className={`font-medium ${notification.isRead ? "text-muted-foreground" : ""}`}>
                        {notification.title}
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {format(notification.createdAt, "MMM d, h:mm a")}
                      </span>
                    </div>
                    <p className="text-sm">{notification.message}</p>
                  </Card>
                  <Separator />
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
